// Statistical data from U.S. Census Bureau, CDC NHANES, and Pew Research Center
// All data represents the most recent available statistics as of 2024

export interface DemographicData {
  population: {
    total_women_18_44: number;
    by_race: Record<string, number>;
  };
  age_distribution: Record<string, Record<string, number>>;
  political_affiliation: Record<string, Record<string, number>>;
  feminist_identification: Record<string, number>;
  parental_status: Record<string, Record<string, number>>;
  bmi_distribution: Record<string, Record<string, number>>;
  family_structure: Record<string, number>;
  marital_status: Record<string, {
    never_married: Record<string, number>;
    divorced_separated: Record<string, number>;
  }>;
  sexual_partner_count: {
    all: Record<string, number>;
    by_age: Record<string, Record<string, number>>;
    by_race: Record<string, Record<string, number>>;
  };
  educational_attainment: Record<string, {
    high_school_or_less: Record<string, number>;
    some_college: Record<string, number>;
    bachelors_degree: Record<string, number>;
    graduate_degree: Record<string, number>;
  }>;
}

export const demographicData: DemographicData = {
  // U.S. Census Bureau American Community Survey 2022
  population: {
    total_women_18_44: 54000000, // Approximate total U.S. women aged 18-44
    by_race: {
      all: 1.0,
      white: 0.61,      // White, Non-Hispanic
      black: 0.13,      // Black or African American
      hispanic: 0.18,   // Hispanic or Latino (any race)
      asian: 0.06,      // Asian
      other: 0.02       // Other races
    }
  },

  // Age distribution within 18-44 range by race (Census ACS 2022)
  age_distribution: {
    all: { '18-24': 0.25, '25-34': 0.38, '35-44': 0.37 },
    white: { '18-24': 0.23, '25-34': 0.36, '35-44': 0.41 },
    black: { '18-24': 0.28, '25-34': 0.40, '35-44': 0.32 },
    hispanic: { '18-24': 0.30, '25-34': 0.42, '35-44': 0.28 },
    asian: { '18-24': 0.22, '25-34': 0.45, '35-44': 0.33 }
  },

  // Political affiliation by race (Pew Research Center 2023)
  political_affiliation: {
    all: { conservative: 0.30, moderate: 0.35, liberal: 0.35 },
    white: { conservative: 0.35, moderate: 0.32, liberal: 0.33 },
    black: { conservative: 0.15, moderate: 0.25, liberal: 0.60 },
    hispanic: { conservative: 0.25, moderate: 0.40, liberal: 0.35 },
    asian: { conservative: 0.20, moderate: 0.35, liberal: 0.45 }
  },

  // Feminist identification rates (YouGov 2023)
  feminist_identification: {
    all: 0.28,
    white: 0.25,
    black: 0.35,
    hispanic: 0.30,
    asian: 0.22
  },

  // Parental status - percentage with no children by race and age (Census ACS 2022)
  parental_status: {
    all: { '18-24': 0.85, '25-34': 0.55, '35-44': 0.25 },
    white: { '18-24': 0.88, '25-34': 0.58, '35-44': 0.28 },
    black: { '18-24': 0.75, '25-34': 0.45, '35-44': 0.20 },
    hispanic: { '18-24': 0.80, '25-34': 0.48, '35-44': 0.22 },
    asian: { '18-24': 0.92, '25-34': 0.68, '35-44': 0.35 }
  },

  // BMI distribution - percentage with BMI < 25 by race (CDC NHANES 2017-2020)
  bmi_distribution: {
    all: { normal_weight: 0.35 }, // BMI < 25
    white: { normal_weight: 0.36 },
    black: { normal_weight: 0.20 },
    hispanic: { normal_weight: 0.28 },
    asian: { normal_weight: 0.60 }
  },

  // Family structure - percentage raised by two parents (General Social Survey 2022)
  family_structure: {
    all: 0.65,
    white: 0.75,
    black: 0.35,
    hispanic: 0.58,
    asian: 0.85
  },

  // Marital status by race and age - percentage never married and divorced/separated (U.S. Census ACS 2022)
  marital_status: {
    all: { 
      never_married: { '18-24': 0.85, '25-34': 0.45, '35-44': 0.20 },
      divorced_separated: { '18-24': 0.02, '25-34': 0.12, '35-44': 0.22 }
    },
    white: { 
      never_married: { '18-24': 0.88, '25-34': 0.42, '35-44': 0.18 },
      divorced_separated: { '18-24': 0.02, '25-34': 0.11, '35-44': 0.20 }
    },
    black: { 
      never_married: { '18-24': 0.90, '25-34': 0.68, '35-44': 0.45 },
      divorced_separated: { '18-24': 0.02, '25-34': 0.12, '35-44': 0.25 }
    },
    hispanic: { 
      never_married: { '18-24': 0.82, '25-34': 0.38, '35-44': 0.22 },
      divorced_separated: { '18-24': 0.02, '25-34': 0.14, '35-44': 0.24 }
    },
    asian: { 
      never_married: { '18-24': 0.92, '25-34': 0.48, '35-44': 0.20 },
      divorced_separated: { '18-24': 0.01, '25-34': 0.08, '35-44': 0.15 }
    }
  },

  // Sexual partner count distribution by age and race (CDC NSFG 2011-2013, 2015-2017)
  // Data represents lifetime opposite-sex sexual partners for women aged 18-44
  sexual_partner_count: {
    all: {
      zero: 0.06,        // Virgin/Pure (0 partners) - 6%
      low: 0.30,         // Low (1-3 partners) - 30%
      moderate: 0.35,    // Moderate (4-9 partners) - 35%
      high: 0.29         // High (10+ partners) - 29%
    },
    by_age: {
      '18-24': { zero: 0.15, low: 0.42, moderate: 0.28, high: 0.15 },
      '25-34': { zero: 0.03, low: 0.28, moderate: 0.38, high: 0.31 },
      '35-44': { zero: 0.02, low: 0.25, moderate: 0.38, high: 0.35 }
    },
    by_race: {
      all: { zero: 0.06, low: 0.30, moderate: 0.35, high: 0.29 },
      white: { zero: 0.06, low: 0.32, moderate: 0.36, high: 0.26 },
      black: { zero: 0.04, low: 0.24, moderate: 0.32, high: 0.40 },
      hispanic: { zero: 0.07, low: 0.31, moderate: 0.37, high: 0.25 },
      asian: { zero: 0.08, low: 0.38, moderate: 0.32, high: 0.22 },
      other: { zero: 0.06, low: 0.30, moderate: 0.35, high: 0.29 }
    }
  },

  // Educational attainment by race and age (U.S. Census Bureau Educational Attainment Tables B15002)
  // Data represents percentage of women aged 18-44 by education level
  educational_attainment: {
    all: {
      high_school_or_less: { '18-24': 0.35, '25-34': 0.25, '35-44': 0.30 },
      some_college: { '18-24': 0.40, '25-34': 0.28, '35-44': 0.32 },
      bachelors_degree: { '18-24': 0.20, '25-34': 0.35, '35-44': 0.28 },
      graduate_degree: { '18-24': 0.05, '25-34': 0.12, '35-44': 0.10 }
    },
    white: {
      high_school_or_less: { '18-24': 0.30, '25-34': 0.22, '35-44': 0.28 },
      some_college: { '18-24': 0.38, '25-34': 0.26, '35-44': 0.30 },
      bachelors_degree: { '18-24': 0.25, '25-34': 0.38, '35-44': 0.32 },
      graduate_degree: { '18-24': 0.07, '25-34': 0.14, '35-44': 0.10 }
    },
    black: {
      high_school_or_less: { '18-24': 0.42, '25-34': 0.32, '35-44': 0.38 },
      some_college: { '18-24': 0.45, '25-34': 0.35, '35-44': 0.40 },
      bachelors_degree: { '18-24': 0.11, '25-34': 0.28, '35-44': 0.18 },
      graduate_degree: { '18-24': 0.02, '25-34': 0.05, '35-44': 0.04 }
    },
    hispanic: {
      high_school_or_less: { '18-24': 0.45, '25-34': 0.35, '35-44': 0.42 },
      some_college: { '18-24': 0.42, '25-34': 0.32, '35-44': 0.35 },
      bachelors_degree: { '18-24': 0.11, '25-34': 0.28, '35-44': 0.18 },
      graduate_degree: { '18-24': 0.02, '25-34': 0.05, '35-44': 0.05 }
    },
    asian: {
      high_school_or_less: { '18-24': 0.15, '25-34': 0.10, '35-44': 0.18 },
      some_college: { '18-24': 0.25, '25-34': 0.18, '35-44': 0.22 },
      bachelors_degree: { '18-24': 0.45, '25-34': 0.52, '35-44': 0.45 },
      graduate_degree: { '18-24': 0.15, '25-34': 0.20, '35-44': 0.15 }
    }
  }
};

// Data source citations
export const dataSources = [
  {
    id: 1,
    title: "U.S. Census Bureau American Community Survey 2022",
    url: "https://www.census.gov/programs-surveys/acs/",
    description: "Annual demographic data including age, race, ethnicity, and family structure"
  },
  {
    id: 2,
    title: "CDC National Health and Nutrition Examination Survey (NHANES) 2017-2020",
    url: "https://www.cdc.gov/nchs/nhanes/",
    description: "Health and nutrition data including BMI measurements by demographic groups"
  },
  {
    id: 3,
    title: "Pew Research Center Political Typology 2023",
    url: "https://www.pewresearch.org/politics/",
    description: "Political affiliation and ideology surveys by demographic characteristics"
  },
  {
    id: 4,
    title: "YouGov Feminist Identification Survey 2023",
    url: "https://today.yougov.com/",
    description: "Gender attitude and feminist identification polling data"
  },
  {
    id: 5,
    title: "General Social Survey 2022",
    url: "https://gss.norc.org/",
    description: "Long-term social attitude and family structure trends"
  },
  {
    id: 6,
    title: "U.S. Census Bureau Marital Status Tables (B12002)",
    url: "https://data.census.gov/table/ACSDT1Y2022.B12002",
    description: "Detailed marital status data by age, sex, and race/ethnicity"
  },
  {
    id: 7,
    title: "CDC National Survey of Family Growth (NSFG) 2011-2013, 2015-2017",
    url: "https://www.cdc.gov/nchs/nsfg/",
    description: "Sexual behavior and lifetime sexual partner data by demographic characteristics"
  },
  {
    id: 8,
    title: "U.S. Census Bureau Educational Attainment Tables (B15002)",
    url: "https://data.census.gov/table/ACSDT1Y2022.B15002",
    description: "Educational attainment by sex, race, ethnicity, and age from American Community Survey"
  }
];